import { useDeepCompareMemo } from 'use-deep-compare';
import { useAppSelector } from '../../../app/hooks';
import {
  AIClass,
  CubeDimensionEnum,
  CubeMeasureEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  FeatureAliases,
  findHighestModelVersion,
  mapToAlias,
} from '../../../services/data/impl/cube/cubeUtils';
import { useFeatureFlags } from '../../../services/featureFlags/hooks/useFeatureFlags';
import { isFeatureEnum } from '../../commonUtils';
import { selectCurrFeature } from '../../select/selectSlice';
import { addIfUnique } from '../utils';
import { CustomerSummary } from './useCustomerSummaryQuery';

// Mapping to exclude certain measures for specific features
const FeatureMeasuresExcludesMap: { [key: string]: CubeMeasureEnum[] } = {
  [FeatureEnum.clusters]: [
    CubeMeasureEnum.DetectionFactHasColorHSV,
    CubeMeasureEnum.DetectionFactAvgLength,
  ],
  [FeatureEnum.earlyCluster]: [
    CubeMeasureEnum.DetectionFactHasColorHSV,
    CubeMeasureEnum.DetectionFactAvgLength,
  ],
  [FeatureEnum.blueberry]: [CubeMeasureEnum.DetectionFactHasColorHSV],
  [FeatureEnum.bud]: [
    CubeMeasureEnum.DetectionFactHasColorHSV,
    CubeMeasureEnum.DetectionFactAvgLength,
  ],
};

// Pre-defined array of measures to check in the customer summary
export const measuresToCheck: (keyof CustomerSummary & CubeMeasureEnum)[] = [
  CubeMeasureEnum.detectionFactAvgCountPerMeter,
  CubeMeasureEnum.DetectionFactAvgLength,
  CubeMeasureEnum.DetectionFactHasColorHSV,
];

interface AvailableServicesAndMeasures {
  availableServices: FeatureEnum[];
  availableServicesRaw: AIClass[];
  availableMeasures: CubeMeasureEnum[];
  currService: AIClass | null;
}

/**
 * A custom hook for finding available services and measures from a filtered customer summary,
 * as well as determining the highest model version for the current feature.
 *
 * This hook calculates lists of unique available services (`FeatureEnum`), full raw service
 * information (`AIClass`), and measures (`CubeMeasureEnum`). It also identifies the highest
 * model version for the current feature (`AIClass | null`).
 *
 * @param {CustomerSummary[] | null} filteredCustomerSummary - The filtered customer summary data.
 * @returns {AvailableServicesAndMeasures} An object containing arrays of unique available services,
 * measures, full raw service information, and the highest model version for the current feature.
 */
const useFindAvailableServicesAndMeasures = (
  filteredCustomerSummary: CustomerSummary[] | null
) => {
  const currFeature = useAppSelector(selectCurrFeature);
  const { Diagnostics } = useFeatureFlags();

  const servicesAndMeasures = useDeepCompareMemo(() => {
    const availableServices: FeatureEnum[] = [];
    const availableServicesRaw: AIClass[] = [];
    const availableMeasures: CubeMeasureEnum[] = [];
    let currService: AIClass | null = null;

    if (filteredCustomerSummary && filteredCustomerSummary.length > 0) {
      filteredCustomerSummary.forEach((customerSummary) => {
        const className = customerSummary[CubeDimensionEnum.className];

        if (isFeatureEnum(className)) {
          addIfUnique(availableServices, mapToAlias(className));
          if (
            isFeatureEnum(customerSummary[CubeDimensionEnum.modelName], true)
          ) {
            const rawServiceInfo: AIClass = {
              [CubeDimensionEnum.className]: className,
              [CubeDimensionEnum.modelName]:
                customerSummary[CubeDimensionEnum.modelName],
              [CubeDimensionEnum.modelVersion]:
                customerSummary[CubeDimensionEnum.modelVersion],
            };
            addIfUnique(availableServicesRaw, rawServiceInfo);
          }
        }

        // Convert excluded measures to a Set for O(1) lookup time
        const excludedMeasuresSet = new Set(
          FeatureMeasuresExcludesMap[currFeature] || []
        );

        measuresToCheck.forEach((measure) => {
          const measureValue = customerSummary[measure];
          if (
            // measure can be 0 so we need to check for null and undefined, otherwise 0 values might be accidentally filtered out
            measureValue !== null &&
            measureValue !== undefined &&
            !excludedMeasuresSet.has(measure)
          ) {
            // / Special case for color HSV to ensure it's true before adding
            if (
              measure === CubeMeasureEnum.DetectionFactHasColorHSV &&
              !measureValue
            ) {
              return; // Skip adding if the value is not true
            }
            addIfUnique(availableMeasures, measure);
          }
        });

        if (availableServicesRaw.length > 0 && currFeature) {
          const featureAliases = FeatureAliases[currFeature] || [currFeature];
          currService = findHighestModelVersion(
            availableServicesRaw,
            featureAliases
          );
        }
      });
    }

    const values: AvailableServicesAndMeasures = {
      // FIXME: Trunks are temporary hidden because they requires bunch of work for correct display
      availableServices: availableServices.filter(
        (service) => Diagnostics || service !== FeatureEnum.trunks
      ),
      availableServicesRaw: availableServicesRaw.filter(
        (service) =>
          Diagnostics ||
          service[CubeDimensionEnum.className] !== FeatureEnum.trunks
      ),
      availableMeasures: availableMeasures,
      currService,
    };

    return values;
  }, [filteredCustomerSummary, currFeature, Diagnostics]);

  return servicesAndMeasures;
};

export default useFindAvailableServicesAndMeasures;
